// A frame's camera for the photo steps: where it is (camera → world, as the editor draws the frame: T · E(f) · pose),
// its photo's intrinsics (the depth grid's K scaled back up, undoing prep/images.py depth_K) and lens distortion
// (distort.ts, once calibrated); projecting world points into it, rays out of it, depth lookups that refuse to
// blend across a depth jump, and the masks that keep the feature steps to the selection or to the planes (the
// frame's own points, which came from its depth pixels, splatted back onto them). Pure.
import { type Rigid, apply, inverse } from '../align.ts';
import type { V3 } from '../math.ts';

/** Radial lens distortion of normalised coordinates: x' = x (1 + k1 r² + k2 r⁴); r2max: beyond this r² it is not known (outside the photo). */
export interface Dist { k1: number; k2: number; r2max?: number }

/** d is monotonic (the lens doesn't fold the image back) out to radius r. */
export function monotonic(d: Dist, r: number): boolean {
  for (let k = 1; k <= 50; k++) { const x2 = ((k / 50) * r) ** 2; if (1 + 3 * d.k1 * x2 + 5 * d.k2 * x2 * x2 <= 0) return false; }
  return true;
}

export interface PhotoCam {
  /** '<scan>:<frame>'. */
  key: string; scan: string; f: number;
  /** Photo and depth grid sizes. */
  w: number; h: number; dw: number; dh: number;
  /** Photo intrinsics [fx, fy, cx, cy] (pixel centres at integers) and the depth grid's. */
  K: number[]; Kd: number[];
  /** Camera (x right, y down, z forward) → world. */
  P: Rigid;
  dist?: Dist | null;
}

/** The photo's intrinsics from its depth grid's (prep/images.py depth_K, undone). */
export function rgbK(Kd: number[], w: number, h: number, dw: number, dh: number): number[] {
  const sx = w / dw, sy = h / dh;
  return [Kd[0] * sx, Kd[1] * sy, (Kd[2] + 0.5) * sx - 0.5, (Kd[3] + 0.5) * sy - 0.5];
}

/** x (1 + k1 r² + k2 r⁴) of normalised (x, y). */
export function distortN(x: number, y: number, d?: Dist | null): [number, number] {
  if (!d || (!d.k1 && !d.k2)) return [x, y];
  const r2 = x * x + y * y, s = 1 + d.k1 * r2 + d.k2 * r2 * r2;
  return [x * s, y * s];
}

/** The inverse of distortN (Newton on the radius). */
export function undistortN(x: number, y: number, d?: Dist | null): [number, number] {
  if (!d || (!d.k1 && !d.k2)) return [x, y];
  const rd = Math.hypot(x, y);
  if (rd < 1e-12) return [x, y];
  let r = rd;
  for (let i = 0; i < 8; i++) {
    const r2 = r * r, f = r * (1 + d.k1 * r2 + d.k2 * r2 * r2) - rd, df = 1 + 3 * d.k1 * r2 + 5 * d.k2 * r2 * r2;
    const dr = f / df;
    r -= dr;
    if (Math.abs(dr) < 1e-12) break;
  }
  return [(x * r) / rd, (y * r) / rd];
}

/** World point X in the camera's photo: [u, v, depth along z], or null behind it. */
export function project(c: PhotoCam, X: ArrayLike<number>, Pinv: Rigid = inverse(c.P)): [number, number, number] | null {
  const q = apply(Pinv, [X[0], X[1], X[2]]);
  if (q[2] <= 1e-6) return null;
  const nx = q[0] / q[2], ny = q[1] / q[2];
  if (c.dist?.r2max && nx * nx + ny * ny > c.dist.r2max) return null;   // well outside the photo: the lens model says nothing there
  const [x, y] = distortN(nx, ny, c.dist);
  return [c.K[0] * x + c.K[2], c.K[1] * y + c.K[3], q[2]];
}

/** Normalised camera coordinates of photo pixel (u, v), undistorted. */
export function normOf(c: PhotoCam, u: number, v: number): [number, number] {
  return undistortN((u - c.K[2]) / c.K[0], (v - c.K[3]) / c.K[1], c.dist);
}

/** The ray through photo pixel (u, v): its origin (the camera centre) and unit direction, in world. */
export function ray(c: PhotoCam, u: number, v: number): { o: V3; d: V3 } {
  const [x, y] = normOf(c, u, v), R = c.P.R, l = Math.hypot(x, y, 1);
  return { o: [...c.P.t] as V3, d: [(R[0] * x + R[1] * y + R[2]) / l, (R[3] * x + R[4] * y + R[5]) / l, (R[6] * x + R[7] * y + R[8]) / l] };
}

/** Photo pixel (u, v) on the depth grid (pixel centres lined up). */
export const toDepthPx = (c: PhotoCam, u: number, v: number): [number, number] => [((u + 0.5) * c.dw) / c.w - 0.5, ((v + 0.5) * c.dh) / c.h - 0.5];

/**
 * Depth (camera z, m) at photo pixel (u, v): bilinear over the 4 depth pixels around it, or NaN when one has
 * none or they differ by more than jump (relative): a feature on a depth edge has no one depth.
 */
export function depthAt(c: PhotoCam, z: ArrayLike<number>, u: number, v: number, jump = 0.03): number {
  const [x, y] = toDepthPx(c, u, v), x0 = Math.floor(x), y0 = Math.floor(y);
  if (x0 < 0 || y0 < 0 || x0 >= c.dw - 1 || y0 >= c.dh - 1) return NaN;
  const i = y0 * c.dw + x0, a = z[i], b = z[i + 1], d = z[i + c.dw], e = z[i + c.dw + 1];
  if (!(a > 0 && b > 0 && d > 0 && e > 0)) return NaN;
  const lo = Math.min(a, b, d, e), hi = Math.max(a, b, d, e);
  if (hi - lo > jump * lo) return NaN;
  const fx = x - x0, fy = y - y0;
  return (a * (1 - fx) + b * fx) * (1 - fy) + (d * (1 - fx) + e * fx) * fy;
}

/** The nearer depth around (u, v) (the foreground side of a jump), NaN when there is none. */
export function nearDepth(c: PhotoCam, z: ArrayLike<number>, u: number, v: number): number {
  const [x, y] = toDepthPx(c, u, v), x0 = Math.round(x), y0 = Math.round(y);
  let best = Infinity;
  for (let j = -1; j <= 1; j++) for (let k = -1; k <= 1; k++) {
    const xx = x0 + k, yy = y0 + j;
    if (xx < 0 || yy < 0 || xx >= c.dw || yy >= c.dh) continue;
    const d = z[yy * c.dw + xx];
    if (d > 0 && d < best) best = d;
  }
  return best < Infinity ? best : NaN;
}

/** The world point seen at photo pixel (u, v) at camera depth d. */
export function backproject(c: PhotoCam, u: number, v: number, d: number): V3 {
  const [x, y] = normOf(c, u, v);
  return apply(c.P, [x * d, y * d, d]);
}

/** Where a ray meets a plane (n·X + w = 0): the distance along it, or NaN when (nearly) parallel or behind. */
export function rayPlane(o: V3, d: V3, eq: ArrayLike<number>): number {
  const den = eq[0] * d[0] + eq[1] * d[1] + eq[2] * d[2];
  if (Math.abs(den) < 1e-6) return NaN;
  const t = -(eq[0] * o[0] + eq[1] * o[1] + eq[2] * o[2] + eq[3]) / den;
  return t > 0 ? t : NaN;
}

/**
 * A byte per depth pixel from the frame's own points: val(i) (0: skip) of each point i of frame f, splatted on the
 * depth pixel it came from (projected with the frame's camera), then spread to the pixels its grid step covers.
 * P: world positions; frame: per point, its frame.
 */
export function splat(c: PhotoCam, P: Float32Array, frame: Uint32Array, val: (i: number) => number): Uint8Array {
  const out = new Uint8Array(c.dw * c.dh), Pinv = inverse(c.P), R = Pinv.R, t = Pinv.t;
  const fx = c.Kd[0], fy = c.Kd[1], cx = c.Kd[2], cy = c.Kd[3];
  let pts = 0;
  for (let i = 0; i < frame.length; i++) {
    if (frame[i] !== c.f) continue;
    pts++;
    const v = val(i);
    if (!v) continue;
    const x = P[3 * i], y = P[3 * i + 1], zz = P[3 * i + 2];
    const qx = R[0] * x + R[1] * y + R[2] * zz + t[0], qy = R[3] * x + R[4] * y + R[5] * zz + t[1], qz = R[6] * x + R[7] * y + R[8] * zz + t[2];
    if (qz <= 1e-6) continue;
    const u = Math.round((fx * qx) / qz + cx), w = Math.round((fy * qy) / qz + cy);
    if (u >= 0 && w >= 0 && u < c.dw && w < c.dh) out[w * c.dw + u] = v;
  }
  // a point stands for a block of depth pixels (the build's decimation): spread each by about half of one
  const step = pts ? Math.sqrt((c.dw * c.dh) / pts) : 1, r = Math.max(1, Math.ceil(step / 2));
  return spread(out, c.dw, c.dh, r);
}

/** Labels spread r pixels into unlabelled neighbours (8-connected; a label keeps what it has; ties: the first found). */
export function spread(m: Uint8Array, w: number, h: number, r: number): Uint8Array {
  let cur = m;
  for (let k = 0; k < r; k++) {
    const nx = cur.slice();
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = y * w + x;
      if (cur[i]) continue;
      let v = 0;
      for (let j = -1; j <= 1 && !v; j++) for (let k = -1; k <= 1 && !v; k++) {
        const xx = x + k, yy = y + j;
        if (xx >= 0 && yy >= 0 && xx < w && yy < h) v = cur[yy * w + xx];
      }
      if (v) nx[i] = v;
    }
    cur = nx;
  }
  return cur;
}

